import { useState, useReducer, useRef, useEffect, useMemo } from "react"
import { select } from 'd3-selection'
import { axisBottom, axisLeft } from 'd3-axis'
import { scaleLinear } from 'd3-scale'
import { zoom } from 'd3-zoom'
import { useFetchProtPsms, useFetchPsm, useMsCoverage, useUnimod } from '../hooks.js'
import Select from "../widgets/Select.jsx";
import { downloadFile, decoratedlist, horizontalList, HeaderWithModal, Modal } from "../utils.jsx"

const margin = {top: 50, right: 30, bottom: 75, left: 40}
const width = 1000 - margin.left - margin.right
const height = 300 - margin.top - margin.bottom
const ppmTrackHeight = 45
const ppmTrackTop = height + 30
const experimentNameMaxLength = 28

const sanitizeSpectrumTitle = (title) =>
  `${title,split('scan=').slice(-1)[0]}`

// Classify a peptide's tryptic status against its parent protein sequence.
// Tryptic rule: cleave C-terminal to K or R, but not when followed by P.
// N-term is tryptic iff peptide starts at protein N-terminus OR preceding
// residue is K/R (and peptide's first residue is not P).
// C-term is tryptic iff peptide ends at protein C-terminus OR ends with K/R
// and the following residue is not P.
// I/L are treated as equivalent when locating the peptide in the protein.
// Returns: 'fully' | 'semi' | 'non' | 'unknown'
export const classifyTrypticity = (peptide, protein) => {
  if (!peptide || !protein) return 'unknown';
  const pep = peptide.toUpperCase().replace(/I/g, 'L');
  const prot = protein.toUpperCase().replace(/I/g, 'L');
  const idx = prot.indexOf(pep);
  if (idx === -1) return 'unknown';

  const prevAA = idx === 0 ? null : prot[idx - 1];
  const lastAA = pep[pep.length - 1];
  const nextAA = idx + pep.length >= prot.length ? null : prot[idx + pep.length];

  const nTerm = prevAA === null || (/[KR]/.test(prevAA) && pep[0] !== 'P');
  const cTerm = nextAA === null || (/[KR]/.test(lastAA) && nextAA !== 'P');

  if (nTerm && cTerm) return 'fully';
  if (nTerm || cTerm) return 'semi';
  return 'non';
};

// Human-readable label for peptide_prot.is_unique
// (0 - shared canonical, 1 - unique canonical, 2 - shared non-canonical,
//  3 - unique non-canonical).
const isUniqueLabel = value => {
  switch (Number(value)) {
    case 0: return "shared peptide between canonical proteins";
    case 1: return "unique to a canonical protein";
    case 2: return "shared between non-canonical proteins";
    case 3: return "unique to a non-canonical protein";
    default: return null;
  }
};

const SpectrumViewer = ({species_sn, protein_seq_id, protein_sequence}) => {
    const [activeSearchTab, setActiveSearchTab] = useState("closed");
    const {isReady: coverageisReady, coverage} = useMsCoverage({species_sn, protein_seq_id, search: activeSearchTab})

    const {isReady, prot_psms} = useFetchProtPsms({species_sn, protein_seq_id, search: activeSearchTab})

    const pmsStateReducer = (state, action) => {
      const { type, payload } = action;
      // A PSM's peptidoform is its modified sequence; fall back to the bare
      // peptide sequence when the modified form wasn't recorded.
      const peptidoformOf = psm => psm.peptidoform || psm.sequence;
      switch (type) {
        case 'init':{
          return {
            selected_psm: {
              study_id: "",
              sequence: "",
              peptidoform: "",
              scan_number: "",
              psm_id: -1
            },
            psms: [],
            spectrum: {},
            spectrum_annotation: {},
            studies: [],
            filteredPeptides: [],
            filteredPeptidoforms: [],
            filteredSpectra: [],
          }
        }
        case 'load_psms':{
          if (!payload || payload.length < 1) {
            // No PSMs for this protein in the selected search: reset to a clean
            // state so stale selections from the other tab don't linger.
            return {
              selected_psm: { study_id: "", sequence: "", peptidoform: "", scan_number: "", psm_id: -1 },
              psms: [],
              spectrum: {},
              spectrum_annotation: {},
              studies: [],
              filteredPeptides: [],
              filteredPeptidoforms: [],
              filteredSpectra: [],
            }
          }
          const best_psm = payload.reduce((prev, current) => (prev.psm_score > current.psm_score) ? prev : current)
          const studyPsms = payload.filter((c) => c.study_id === best_psm.study_id)
          const filteredPeptides = Array.from(new Set(studyPsms.map(item => item.sequence)))
          const peptidePsms = studyPsms.filter((c) => c.sequence === best_psm.sequence)
          const filteredPeptidoforms = Array.from(new Set(peptidePsms.map(peptidoformOf)))
          const selectedPeptidoform = peptidoformOf(best_psm)
          return {
            ...state,
            psms: payload,
            studies: Array.from(new Set(payload.map((item) => item.study_id))),
            filteredPeptides: filteredPeptides,
            filteredPeptidoforms: filteredPeptidoforms,
            filteredSpectra: Array.from(new Set(peptidePsms.filter((c) => peptidoformOf(c) === selectedPeptidoform).map(item => item.scan_number))),
            selected_psm: best_psm,
          };
        }
        case 'select_study': {
          const studyPsms = state.psms.filter((c) => c.study_id === payload.value)
          const selected_psm = studyPsms.reduce((prev, current) => (prev.psm_score > current.psm_score) ? prev : current)
          const filteredPeptides = Array.from(new Set(studyPsms.map(item => item.sequence)))
          const peptidePsms = studyPsms.filter((c) => c.sequence === selected_psm.sequence)
          const filteredPeptidoforms = Array.from(new Set(peptidePsms.map(peptidoformOf)))
          const selectedPeptidoform = peptidoformOf(selected_psm)
          return {
            ...state,
            filteredPeptides: filteredPeptides,
            filteredPeptidoforms: filteredPeptidoforms,
            filteredSpectra: Array.from(new Set(peptidePsms.filter((c) => peptidoformOf(c) === selectedPeptidoform).map(item => item.scan_number))),
            selected_psm: selected_psm,
          };
        }
        case 'select_peptide': {
          const filteredPsms = state.psms.filter((c) => c.study_id === state.selected_psm.study_id && c.sequence === payload.value)
          const filteredPeptidoforms = Array.from(new Set(filteredPsms.map(peptidoformOf)))
          const selected_psm = filteredPsms.reduce((prev, current) => (prev.psm_score > current.psm_score) ? prev : current)
          const selectedPeptidoform = peptidoformOf(selected_psm)
          return {
            ...state,
            filteredPeptidoforms: filteredPeptidoforms,
            filteredSpectra: Array.from(new Set(filteredPsms.filter((c) => peptidoformOf(c) === selectedPeptidoform).map(item => item.scan_number))),
            selected_psm: selected_psm,
          };
        }
        case 'select_peptidoform': {
          const selectedPeptidoform = payload.value
          const filteredPsms = state.psms.filter((c) => c.study_id === state.selected_psm.study_id && c.sequence === state.selected_psm.sequence && peptidoformOf(c) === selectedPeptidoform)
          if (filteredPsms.length < 1) {
            return { ...state }
          }
          const selected_psm = filteredPsms.reduce((prev, current) => (prev.psm_score > current.psm_score) ? prev : current)
          return {
            ...state,
            filteredSpectra: Array.from(new Set(filteredPsms.map(item => item.scan_number))),
            selected_psm: selected_psm,
          };
        }
        case 'select_spectrum': {
          const selectedPeptidoform = peptidoformOf(state.selected_psm)
          const matches = state.psms.filter((c) => c.study_id === state.selected_psm.study_id && c.sequence === state.selected_psm.sequence && peptidoformOf(c) === selectedPeptidoform && c.scan_number === payload.value)
          if (matches.length < 1) {
            return { ...state }
          }
          const selected_psm = matches.reduce((prev, current) => (prev.psm_score > current.psm_score) ? prev : current)
          return {
            ...state,
            selected_psm: selected_psm,
          };
        }
        default:
          return { ...state };
      }
    };

    const [psmStateView, dispatchPsmStates] = useReducer(pmsStateReducer, pmsStateReducer(null, {type:'init', payload:null}));

    useEffect(() => {dispatchPsmStates({type: 'load_psms', payload: prot_psms})}, [isReady, prot_psms]);

    const {isReady:psmIsReady, psm, fetchPsm} = useFetchPsm()

    // Descriptive metadata for Unimod modifications, keyed by accession, from
    // the species' unimod DB. Used to show the canonical modification
    // description in the modified-peptide table.
    const {rows: unimodRows} = useUnimod({species_sn})
    const unimodById = useMemo(() => {
        const m = new Map();
        for (const u of unimodRows || []) {
            if (u.unimod_id != null) m.set(Number(u.unimod_id), u);
        }
        return m;
    }, [unimodRows]);

    // Map each mod_mass_spec_id to the set of Unimod IDs that share the same
    // (specificity, position_scope, mass). In DMO / mass-offset searches the
    // search engine can only distinguish PTMs by mass, not identity, so all
    // entries in the same class are equally valid candidates for a given site.
    const unimodByMassClass = useMemo(() => {
        const m = new Map();
        for (const u of unimodRows || []) {
            if (u.mod_mass_spec_id == null || u.unimod_id == null) continue;
            if (!m.has(u.mod_mass_spec_id)) m.set(u.mod_mass_spec_id, new Set());
            m.get(u.mod_mass_spec_id).add(Number(u.unimod_id));
        }
        return m;
    }, [unimodRows]);

    // Reverse lookup: unimod_id → all IDs in the same mass class (only for
    // classes with >1 entry, to avoid cluttering single-entry lookups).
    const unimodIdToCandidates = useMemo(() => {
        const m = new Map();
        for (const ids of unimodByMassClass.values()) {
            if (ids.size <= 1) continue;
            const arr = [...ids];
            for (const id of arr) m.set(id, arr);
        }
        return m;
    }, [unimodByMassClass]);

    useEffect(() => {
      if (psmStateView.selected_psm.psm_id !== -1) {
        
        fetchPsm(species_sn, psmStateView.selected_psm.psm_id, activeSearchTab)
      }
    }, [psmStateView.selected_psm.psm_id])

    const [currentZoomState, setCurrentZoomState] = useState();
    const svgRef = useRef();

    useEffect(() => {
      if (!psmIsReady || !svgRef.current){
        return;
      }
      const spectrum = psm.spectrum
      const spectrum_annotation = psm.spec_ann

      const svg = select(svgRef.current);
      const xScale = scaleLinear().domain(
          [Math.min(...spectrum["m/z"]), Math.max(...spectrum["m/z"])]
        ).range([margin.left, width]);

      if (currentZoomState) {
        const newXScale = currentZoomState.rescaleX(xScale);
        xScale.domain(newXScale.domain());
      }

      const yScale = scaleLinear().domain([0, 1]).range([height, margin.top]);

      const xAxis = axisBottom(xScale);
      const yAxis = axisLeft(yScale);
      svg.select(".x-axis").style("transform", "translateY("+height+"px)").call(xAxis);
      svg.select(".y-axis").style("transform", "translateX("+margin.left+"px)").call(yAxis);

      function getRelInt(intensity) {
        return (intensity / Math.max(...spectrum.intensity));
      }

      function getPeaksD(mz, intensity) {
        return (`M${xScale(mz)},${yScale(0)}L${xScale(mz)},${yScale(getRelInt(intensity))}`);
      }

      function getPeakColor(data) {
        switch (data.Subtype) {
          case "y":
            return ("red");
          case "b":
            return ("blue");
        }
      }

      function getPpmError(data) {
        return (data["m/z Error (Da)"] / data["Theoretic m/z"]) * 1000000;
      }

      const ppmErrors = spectrum_annotation.peak_anns
        .map(getPpmError)
        .filter(Number.isFinite);
      const maxAbsPpmError = ppmErrors.length > 0 ? Math.max(...ppmErrors.map(Math.abs)) : 0;
      const ppmLimit = Math.max(5, Math.ceil(maxAbsPpmError));
      const ppmScale = scaleLinear()
        .domain([-ppmLimit, ppmLimit])
        .range([ppmTrackTop + ppmTrackHeight, ppmTrackTop]);
      const ppmTickValues = ppmScale.ticks(5);

      svg
        .selectAll(".peak-bg")
        .data(spectrum["m/z"])
        .join("path")
        .attr("class", "peak-bg")
        .attr("d", (value, idx) => getPeaksD(value, spectrum.intensity[idx]))
        .attr("fill", "none")
        .attr("stroke", "grey");
        
      svg
        .selectAll(".tooltip-ion-name")
        .data(spectrum_annotation.peak_anns)
        .join("text")
        .attr("class", "tooltip-ion-name")
        .text(value => value.Name)
        .attr("text-anchor", "middle")
        .style("fill", value => getPeakColor(value))
        .attr("x", value => xScale(value["m/z"]))
        .attr("y", value => yScale(getRelInt(value.Intensity)) - 8)
        .on("mouseenter", value => {
          svg
            .selectAll(".tooltip-bg")
            .data([value])
            .join("rect")
            .attr("class", "tooltip-bg")
            .attr("width", 150)
            .attr("height", 50)
            .attr("fill", "white")
            .attr("x", xScale(value.srcElement.__data__["m/z"]) + 18)
            .attr("y", yScale(getRelInt(value.srcElement.__data__.Intensity)) - 38);
          svg
            .selectAll(".tooltip-charge")
            .data([value])
            .join("text")
            .attr("class", "tooltip-charge")
            .text(`charge: ${value.srcElement.__data__["Fragment Charge"]}+`)
            .attr("x", xScale(value.srcElement.__data__["m/z"]) + 18)
            .attr("y", yScale(getRelInt(value.srcElement.__data__.Intensity)) - 24);
          svg
            .selectAll(".tooltip-mz")
            .data([value])
            .join("text")
            .attr("class", "tooltip-mz")
            .text(`m/z: ${value.srcElement.__data__["m/z"]}`)
            .attr("x", xScale(value.srcElement.__data__["m/z"]) + 18)
            .attr("y", yScale(getRelInt(value.srcElement.__data__.Intensity)) - 8);
          svg
            .selectAll(".tooltip-abundance")
            .data([value])
            .join("text")
            .attr("class", "tooltip-abundance")
            .text(`rel. abundance: ${Math.round(getRelInt(value.srcElement.__data__.Intensity) * 100)/100}`)
            .attr("x", xScale(value.srcElement.__data__["m/z"]) + 18)
            .attr("y", yScale(getRelInt(value.srcElement.__data__.Intensity)) + 8);
        })
        .on("mouseleave", () => {
          svg.select(".tooltip-charge").remove();
          svg.select(".tooltip-mz").remove();
          svg.select(".tooltip-abundance").remove();
          svg.select(".tooltip-bg").remove();
        });

      svg
        .selectAll(".peak-ann")
        .data(spectrum_annotation.peak_anns)
        .join("path")
        .attr("class", "peak-ann")
        .attr("d", value => getPeaksD(value["m/z"], value.Intensity))
        .attr("fill", "none")
        .attr("stroke-width", 2)
        .attr("stroke", value => getPeakColor(value))
        .on("mouseenter", value => {
          svg
            .selectAll(".tooltip-charge")
            .data([value])
            .join("text")
            .attr("class", "tooltip-charge")
            .text(`charge: ${value.srcElement.__data__["Fragment Charge"]}+`)
            .attr("x", xScale(value.srcElement.__data__.mz) + 18)
            .attr("y", yScale(getRelInt(value.srcElement.__data__.Intensity)) - 24);
          svg
            .selectAll(".tooltip-mz")
            .data([value])
            .join("text")
            .attr("class", "tooltip-mz")
            .text(`m/z: ${value.srcElement.__data__.mz}`)
            .attr("x", xScale(value.srcElement.__data__.mz) + 18)
            .attr("y", yScale(getRelInt(value.srcElement.__data__.Intensity)) - 8);
          svg
            .selectAll(".tooltip-abundance")
            .data([value])
            .join("text")
            .attr("class", "tooltip-abundance")
            .text(`rel. abundance: ${Math.round(getRelInt(value.srcElement.__data__.Intensity) * 100)/100}`)
            .attr("x", xScale(value.srcElement.__data__.mz) + 18)
            .attr("y", yScale(getRelInt(value.srcElement.__data__.Intensity)) + 8);
        })
        .on("mouseleave", () => {
          svg.select(".tooltip-charge").remove();
          svg.select(".tooltip-mz").remove();
          svg.select(".tooltip-abundance").remove();
        });

      svg
        .selectAll(".ppm-zero-line")
        .data([0])
        .join("line")
        .attr("class", "ppm-zero-line")
        .attr("x1", margin.left)
        .attr("x2", width)
        .attr("y1", ppmScale(0))
        .attr("y2", ppmScale(0))
        .attr("stroke", "#9ca3af")
        .attr("stroke-dasharray", "3 3");

      svg
        .selectAll(".ppm-axis")
        .data([0])
        .join("g")
        .attr("class", "ppm-axis")
        .style("transform", `translateX(${margin.left}px)`)
        .call(axisLeft(ppmScale).tickValues(ppmTickValues));

      svg
        .selectAll(".ppm-error-dot")
        .data(spectrum_annotation.peak_anns)
        .join("circle")
        .attr("class", "ppm-error-dot")
        .attr("cx", value => xScale(value["m/z"]))
        .attr("cy", value => ppmScale(getPpmError(value)))
        .attr("r", 3)
        .attr("fill", value => getPeakColor(value))
        .attr("stroke", "white")
        .attr("stroke-width", 1)
        .on("mouseenter", value => {
          svg
            .selectAll(".tooltip-ppm")
            .data([value])
            .join("text")
            .attr("class", "tooltip-ppm")
            .text(`error: ${getPpmError(value.srcElement.__data__).toFixed(2)} ppm`)
            .attr("x", xScale(value.srcElement.__data__["m/z"]) + 8)
            .attr("y", ppmScale(getPpmError(value.srcElement.__data__)) - 8);
        })
        .on("mouseleave", () => {
          svg.select(".tooltip-ppm").remove();
        });

      svg
        .selectAll('.x-axis-label')
        .data(['m/z'])
        .join('text')
        .attr('class', 'x-axis-label')
        .attr('x', width/2)
        .attr('y', height + 24)
        .text(v => v)
      svg
        .selectAll('.ppm-axis-label')
        .data(['error (ppm)'])
        .join('text')
        .attr('class', 'ppm-axis-label')
        .attr('x', -ppmScale(0))
        .attr('y', 8)
        .attr('transform', 'rotate(-90)')
        .attr('text-anchor', 'middle')
        .text(v => v)
      svg
        .selectAll('.y-axis-label')
        .data(['rel. intensity'])
        .join('text')
        .attr('class', 'y-axis-label')
        .attr('x', 10)
        .attr('y', height/2 + 60 )
        .text(v => v)
        .attr('transform', `rotate(-90,10,${height/2 + 60})`)
        

      const peptide_position = {
        "x":width - (spectrum_annotation.Sequence.length * 18), 
        "y":12
      };

      svg
        .selectAll(".peptide-frag")
        .data(spectrum_annotation.Sequence)
        .join("text")
        .attr("class", "peptide-frag")
        .attr("fill", "black")
        .attr("x", (value, index) => peptide_position.x + index * 18)
        .attr("y", peptide_position.y)
        .attr("text-anchor", "middle")
        .attr("font-family", "Courier")
        .style("font-size", 16)
        .text(value => value);
      
      function getSeparator(p_ann) {
        switch (p_ann.Subtype) {
          case 'b':// top blue
            var i = p_ann.Number -1
            return (`M${peptide_position.x + 3 + 18*i},${0}L${peptide_position.x + 9 + 18*i},${3}L${peptide_position.x + 9 + 18*i},${10}`);

          case 'y':// bottom red
            var i = spectrum_annotation.Sequence.length - p_ann.Number -1
            return (`M${peptide_position.x + 9 + 18*i},${10}L${peptide_position.x + 9 + 18*i},${18}L${peptide_position.x + 15 + 18*i},${21}`);
        }
      }

      const yb = /y|b/;

      svg
        .selectAll(".frag-sep")
        .data(spectrum_annotation.peak_anns.filter(p => yb.test(p.Name)))
        .join("path")
        .attr("class", "frag-sep")
        .attr("d", value => getSeparator(value)) 
        .attr("fill", "none")
        .attr("stroke", value => getPeakColor(value));
      
      const zoomBehavior = zoom()
        .scaleExtent([1, 5])
        .translateExtent([[0,0], [width, height]])
        .on("zoom", (event) => {
          const zoomState = event.transform;
          setCurrentZoomState(zoomState);
        });
      
      zoomBehavior(svg);

    }, [activeSearchTab, currentZoomState, prot_psms, psm]);

    const downloadPsms = e => {
      
      e.preventDefault()

      let headers = ["psm_id,sequence,experiment_id,study_id,scan_number,psm_score"]
      downloadFile({
        data: [...headers, ...prot_psms.map(r => Object.values(r).join(','))].join('\n'),
        fileName: `PSMs_${protein_seq_id}.csv`,
        fileType: 'text/csv',
      })
    }

    const downloadMgf = e => {
      e.preventDefault()

      let headers = [
        'BEGIN IONS',
        `TITLE=${psm.spectrum.TITLE}`,
        `RTINSECONDS=${psm.spectrum.RTINSECONDS}`,
        `PEPMASS=${psm.spectrum.PEPMASS}`,
        `CHARGE=${psm.spectrum.CHARGE}`]

      downloadFile({
        data: [...headers, ...psm.spectrum['m/z'].map((mz, idx) => `${mz},${psm.spectrum['intensity'][idx]}`), 'END IONS'].join('\n'),
        fileName: `spectrum_${protein_seq_id}_${psm.spectrum.TITLE}.mgf`,
        fileType: 'text',
      })
    }

    if (!isReady) {
      return <SpectrumViewerSkeleton />
    };

    const hasPsms = prot_psms && prot_psms.length > 0
    const searchLabel = activeSearchTab === "dmo" ? "DMO" : "closed"

    return (
      <div className="shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] rounded p-3 mt-3 mr-3">
        <div className="border-b width-full border-slate-200">
          <HeaderWithModal title="Mass Spectrometry">
            <p>Browse peptide spectrum matches (PSMs) resulting from the re-analyses of public mass spectrometry data using the OpenProt protein library.
            <a className="cursor-pointer hover:underline text-blue-600" href='/documentation'> See the documentation</a> for more details on the method.</p>
            <p>Select a study name, a peptide, a peptidoform (modified peptide) and a specific spectrum to see it on the spectrum viewer. The viewer can zoom (by rolling the mouse wheel) and pan (by clicking and dragging).</p>
          </HeaderWithModal>
          <div className="mt-3 flex gap-2">
            <SearchTabButton
              isActive={activeSearchTab === "closed"}
              onClick={() => setActiveSearchTab("closed")}
            >
              Closed search
            </SearchTabButton>
            <SearchTabButton
              isActive={activeSearchTab === "dmo"}
              onClick={() => setActiveSearchTab("dmo")}
            >
              DMO search
            </SearchTabButton>
          </div>
        </div>

        {!hasPsms ? (
          <div className="mt-4 rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-slate-500">
            No {searchLabel} search PSMs are available for this protein.
          </div>
        ) : (
          <>
            <div className="flex">
              {horizontalList('studies', coverage.n_studies ?? 0, null, "number of independent studies" )}
              {horizontalList('unique peptides', coverage.n_detected_unique_peps ?? 0, null, "number of detected unique peptides" )}
              {horizontalList('detected coverage', `${(coverage.detected_coverage ?? 0).toFixed(1)}%`, null, "sequence coverage from detected peptides following peptide assignation rules" )}
            </div>


            <button className='font-medium text-blue-600 dark:text-blue-500 hover:underline' type='button' onClick={downloadPsms}>download list of PSMs</button>

            <div className="grid grid-cols-6 gap-y-4 mb-5 w-full ml-2">
              <div className="col-span-6">
                  <Selector
              items={psmStateView.studies}
              label={"Study"}
              selectedItem={psmStateView.selected_psm.study_id}
              onChange={e =>
                dispatchPsmStates({
                    type: 'select_study',
                    payload: { value: e},
                  })
                }
              /></div>
              <div className="col-span-2"><Selector
                  label={"Peptide"}
              items={psmStateView.filteredPeptides}
              selectedItem={psmStateView.selected_psm.sequence}
              onChange={e =>
                dispatchPsmStates({
                    type: 'select_peptide',
                    payload: { value: e},
                  })
                }
              /></div>
              <div className="col-span-2"><Selector
                  label={"Peptidoform"}
              items={psmStateView.filteredPeptidoforms}
              selectedItem={psmStateView.selected_psm.peptidoform || psmStateView.selected_psm.sequence}
              onChange={e =>
                dispatchPsmStates({
                    type: 'select_peptidoform',
                    payload: { value: e },
                  })
                }
              /></div>
              <div className="col-span-2"><Selector
                  label={"Spectrum"}
              items={psmStateView.filteredSpectra}
              selectedItem={psmStateView.selected_psm.scan_number}
              onChange={e =>
                dispatchPsmStates({
                    type: 'select_spectrum',
                    payload: { value: e },
                  })
                }
              /></div>
              <div className="col-span-6">
                  {psmIsReady && (
                    <>
                      {decoratedlist('precursor charge', `${psm.spec_ann["Measured Charge"]}+`, null, "measured precursor ion charge")}
                      {decoratedlist('PSM score', psmStateView.selected_psm.psm_score, null, "Percolator score output from Ms2Rescore")}
                      {decoratedlist('PEP', psm.PEP.toExponential(2), null, "posterior error probability")}
                      <ExpandableExperimentName value={psmStateView.selected_psm.experiment_id} />
                      <CrossSearchBadge psm={psmStateView.selected_psm} activeSearchTab={activeSearchTab} />
                    </>
                  )}
              </div>
            </div>
              {psmIsReady && <PsmModificationSummary spectrumAnnotation={psm.spec_ann} protein_sequence={protein_sequence} unimodById={unimodById} unimodIdToCandidates={unimodIdToCandidates} isUnique={psmStateView.selected_psm.is_unique} />}
              {psmIsReady ? (
                <svg ref={svgRef} width={width} height={height + margin.top + margin.bottom}>
                  <g className="x-axis" />
                  <g className="y-axis" />
                </svg>
              ) : (
                <div
                  className="flex items-center justify-center text-slate-400 text-sm"
                  style={{width, height: height + margin.top + margin.bottom}}
                >
                  Loading spectrum…
                </div>
              )}
           {psmIsReady && <button className='font-medium text-blue-600 dark:text-blue-500 hover:underline' type='button' onClick={downloadMgf}>download spectrum (mgf)</button>}
          </>
        )}
      </div>
    );
}

const SearchTabButton = ({isActive, onClick, children}) => (
    <button
        type="button"
        className={`rounded-t border border-b-0 px-4 py-2 text-sm font-medium ${isActive ? "border-slate-300 bg-white text-slate-900" : "border-transparent bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
        onClick={onClick}
    >
        {children}
    </button>
)

// Badge indicating whether this PSM's spectrum was also identified in the
// other search type (closed vs DMO). Uses the cross_search_diff value from
// compare_searches_psms: 0 = same PSM in both, 1 = different PSM, 2 = only
// in one search (NULL in the other).
const CrossSearchBadge = ({psm, activeSearchTab}) => {
    const found = psm.found_in_other_search
    const diff = psm.cross_search_diff
    const otherLabel = activeSearchTab === "dmo" ? "closed" : "DMO"

    if (!found) {
        return (
            <span
                title={`Not identified in ${otherLabel} search`}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500"
            >
                Only in {activeSearchTab === "dmo" ? "DMO" : "closed"} search
            </span>
        )
    }

    if (diff === 0) {
        return (
            <span
                title={`Same PSM identified in both closed and DMO searches`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
            >
                Found in both searches
            </span>
        )
    }

    if (diff === 1) {
        return (
            <span
                title={`Different PSM identified in ${otherLabel} search for the same spectrum`}
                className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
            >
                Different PSM in {otherLabel} search
            </span>
        )
    }

    return null
}

// Animated placeholder shown while PSMs / spectrum data load. Mirrors the real
// Mass Spectrometry card layout (header, tabs, stats, selectors, plot area) so
// there is no layout shift once the data arrives.
const Bar = ({className = ""}) => (
    <div className={`rounded bg-slate-200 ${className}`} />
)

const SpectrumViewerSkeleton = () => (
    <div
        role="status"
        aria-busy="true"
        aria-live="polite"
        className="animate-pulse shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] rounded p-3 mt-3 mr-3"
    >
        <span className="sr-only">Loading mass spectrometry data…</span>

        {/* header + tabs */}
        <div className="border-b width-full border-slate-200 pb-3">
            <Bar className="h-5 w-52" />
            <div className="mt-3 flex gap-2">
                <Bar className="h-9 w-32" />
                <Bar className="h-9 w-32" />
            </div>
        </div>

        {/* summary stats */}
        <div className="mt-3 flex gap-8">
            <Bar className="h-10 w-28" />
            <Bar className="h-10 w-36" />
            <Bar className="h-10 w-40" />
        </div>

        {/* selectors row */}
        <div className="mt-4 grid grid-cols-5 gap-3 w-full">
            <Bar className="h-9" />
            <Bar className="h-9" />
            <Bar className="h-9" />
            <div className="col-span-2 space-y-2">
                <Bar className="h-4 w-3/4" />
                <Bar className="h-4 w-2/3" />
                <Bar className="h-4 w-1/2" />
            </div>
        </div>

        {/* modification summary block */}
        <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3 space-y-2">
            <Bar className="h-4 w-40" />
            <Bar className="h-5 w-3/4" />
        </div>

        {/* spectrum plot area (matches svg footprint to avoid layout shift) */}
        <div
            className="mt-3 flex items-end gap-1 overflow-hidden"
            style={{height: height + margin.top + margin.bottom}}
        >
            {Array.from({length: 40}).map((_, i) => (
                <div
                    key={i}
                    className="flex-1 rounded-t bg-slate-200"
                    style={{height: `${15 + ((i * 37) % 80)}%`}}
                />
            ))}
        </div>
    </div>
)


const shortenExperimentName = value => {
    if (!value || value.length <= experimentNameMaxLength) {
        return value;
    }

    const startLength = 14;
    const endLength = 11;
    return `${value.slice(0, startLength)}...${value.slice(-endLength)}`;
}

const ExpandableExperimentName = ({value}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const shouldShorten = value && value.length > experimentNameMaxLength;
    const displayValue = shouldShorten && !isExpanded ? shortenExperimentName(value) : value;

    return (
        <div className="flex">
            <div className="group relative inline-block m-1 w-1/3 pr-2 text-right border-r-2 border-slate-400 font-bold cursor-default">
                <div>experiment</div>
                <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition bg-blue-400 text-white p-1 rounded absolute bottom-full mb-1 text-xs whitespace-nowrap">
                    name of spectrum file within dataset
                </div>
            </div>
            <div className="m-auto ml-2">
                {shouldShorten ? (
                    <button
                        type="button"
                        className="break-all text-left text-black hover:underline"
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-expanded={isExpanded}
                    >
                        {displayValue}
                    </button>
                ) : displayValue}
            </div>
        </div>
    );
}

const parseModificationList = value => {
    if (!value || value === "None" || value === "No" || value === "-") {
        return [];
    }

    return value
        .split(/;(?![^()]*\))/)
        .map(item => item.trim())
        .filter(Boolean)
        .map(label => {
            const positionMatch = label.match(/\s*\((\d+)\)\s*$/);
            const position = positionMatch ? positionMatch[1] : null;
            const cleanLabel = label.replace(/\s*\(\d+\)\s*$/, '');
            return {label: cleanLabel, position};
        });
}

// ---- Unimod resolution -----------------------------------------------------
// Curated map: entries the OpenProt pipeline actually configures in
// ms2_rescore_config.py, plus a handful of commonly-seen modifications.
// The `name` field uses the canonical Unimod interim name.
const UNIMOD = {
    1:   { name: 'Acetyl',                 aliases: ['acetylation'] },
    2:   { name: 'Amidated',               aliases: ['amidation'] },
    4:   { name: 'Carbamidomethyl',        aliases: ['carbamidomethylation'] },
    7:   { name: 'Deamidated',             aliases: ['deamidation'] },
    21:  { name: 'Phospho',                aliases: ['phosphorylation', 'phosphos', 'phosphot', 'phosphoy'] },
    26:  { name: 'Pyro-carbamidomethyl',   aliases: [] },
    27:  { name: 'Glu->pyro-Glu',          aliases: ['glupyroglu', 'pyrolidonefrome', 'pyrolidone from e'] },
    28:  { name: 'Gln->pyro-Glu',          aliases: ['glnpyroglu', 'pyrolidonefromq', 'pyrolidone from q', 'pyrolidone'] },
    34:  { name: 'Methyl',                 aliases: ['methylation'] },
    35:  { name: 'Oxidation',              aliases: [] },
    36:  { name: 'Dimethyl',               aliases: ['dimethylation'] },
    214: { name: 'iTRAQ4plex',             aliases: ['itraq'] },
    259: { name: 'Label:13C(6)',           aliases: ['silack'] },
    267: { name: 'Label:13C(6)15N(4)',     aliases: ['silacr'] },
    385: { name: 'Ammonia-loss',           aliases: [] },
    730: { name: 'iTRAQ8plex',             aliases: [] },
    737: { name: 'TMT6plex',               aliases: ['tmt', 'tmt6plexn', 'tmt10plex', 'tmt11plex'] },
};

const _normUnimodKey = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const NAME_TO_UNIMOD = (() => {
    const m = new Map();
    for (const [id, {name, aliases}] of Object.entries(UNIMOD)) {
        m.set(_normUnimodKey(name), Number(id));
        for (const a of aliases) m.set(_normUnimodKey(a), Number(id));
    }
    return m;
})();

// Strip trailing " of X" / " on X" / " at X" / " from X" / " (X)" site info from a modification label.
const _stripSite = label => label
    .replace(/\s*\(.*?\)\s*$/, '')
    .replace(/\s+(of|on|at|from)\s+.*$/i, '')
    .trim();

// Best-effort mapping of a human-readable modification label to a Unimod accession.
export const resolveUnimodId = label => {
    if (!label) return null;
    const key = _normUnimodKey(_stripSite(label));
    if (!key) return null;
    if (NAME_TO_UNIMOD.has(key)) return NAME_TO_UNIMOD.get(key);
    // Fuzzy fallback: prefix match either way (e.g. "phosphorylation" vs "phospho").
    for (const [k, id] of NAME_TO_UNIMOD.entries()) {
        if (key.startsWith(k) || k.startsWith(key)) return id;
    }
    return null;
};

// Convert an OpenProt modified sequence to ProForma notation.
// Example: "U:214-GNS<U:21>HC<U:4>VPQAPR-COOH"
//       -> "[UNIMOD:214]-GNS[UNIMOD:21]HC[UNIMOD:4]VPQAPR"
// The leading/trailing "N-term-...-C-term" descriptors are split on the outer
// dashes: a "U:N" terminus becomes a ProForma terminal modification, while free
// groups (H, COOH, OH, NH2, ...) are dropped. Inline "<U:N>"/"[U:N]" tokens are
// rewritten as "[UNIMOD:N]" following the residue they decorate.
// See https://github.com/HUPO-PSI/ProForma for the standard.
export const toProForma = seq => {
    if (!seq) return seq;

    const termToTag = t => {
        const m = /^U:(\d+)$/.exec(t.trim());
        return m ? `[UNIMOD:${m[1]}]` : null;
    };

    let nTermTag = null;
    let cTermTag = null;
    let core = seq;

    const parts = seq.split('-');
    if (parts.length >= 3) {
        nTermTag = termToTag(parts[0]);
        cTermTag = termToTag(parts[parts.length - 1]);
        core = parts.slice(1, -1).join('-');
    }

    // Inline modifications: <U:N> or [U:N] -> [UNIMOD:N]
    core = core.replace(/[<\[]U:(\d+)[>\]]/g, '[UNIMOD:$1]');

    return `${nTermTag ? nTermTag + '-' : ''}${core}${cTermTag ? '-' + cTermTag : ''}`;
};

// Extract Unimod IDs from a modified sequence like "K<U:737>PEP[U:35]TIDE".
export const extractUnimodIdsFromSequence = seq => {
    if (!seq) return [];
    const ids = [];
    const re = /[<\[]U:(\d+)[>\]]/g;
    let m;
    while ((m = re.exec(seq)) !== null) ids.push(Number(m[1]));
    return ids;
};

// Walk the modified sequence and return one entry per modification token,
// with the modified residue (uppercase) and its 1-based position on the
// peptide. Tokens that appear before any amino acid are labelled as N-term;
// tokens that appear after the final amino acid are labelled as C-term.
export const extractModificationSites = seq => {
    if (!seq) return [];
    const sites = [];
    const re = /([A-Za-z])|[<\[]U:(\d+)[>\]]/g;
    let aaIndex = 0;
    let lastAA = null;
    let totalAAs = 0;
    // First pass: count amino acids so we can flag C-terminal tokens.
    for (const ch of seq) if (/[A-Za-z]/.test(ch)) totalAAs += 1;
    let m;
    while ((m = re.exec(seq)) !== null) {
        if (m[1]) {
            aaIndex += 1;
            lastAA = m[1].toUpperCase();
        } else {
            const id = Number(m[2]);
            if (aaIndex === 0) {
                sites.push({unimodId: id, aa: 'N-term', position: 'N-term'});
            } else if (aaIndex === totalAAs && /[<\[]U:\d+[>\]]\s*$/.test(seq.slice(0, re.lastIndex))) {
                // Trailing token past the last residue: treat as C-term marker,
                // but still expose the residue it decorates.
                sites.push({unimodId: id, aa: lastAA, position: aaIndex, terminus: 'C-term'});
            } else {
                sites.push({unimodId: id, aa: lastAA, position: aaIndex});
            }
        }
    }
    return sites;
};

const unimodUrl = id => `https://www.unimod.org/modifications_view.php?editid1=${id}`;
const unimodDisplayName = id => (UNIMOD[id] ? UNIMOD[id].name : null);

const UnimodCell = ({id, candidates}) => {
    if (id == null) return <span className="text-slate-400">—</span>;
    // When the same mass (mod_mass_spec_id) maps to several Unimod entries,
    // show every candidate accession as a separate link.
    const ids = candidates && candidates.length > 1 ? candidates : [id];
    return (
        <span className="flex flex-wrap gap-x-1">
            {ids.map(uid => (
                <a
                    key={uid}
                    href={unimodUrl(uid)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                    title="View on unimod.org"
                >
                    U:{uid}
                </a>
            ))}
        </span>
    );
};


const PsmModificationSummary = ({spectrumAnnotation, protein_sequence, unimodById = new Map(), unimodIdToCandidates = new Map(), isUnique}) => {
    const uniqueLabel = isUniqueLabel(isUnique);
    const [showUniqueModal, setShowUniqueModal] = useState(false);
    const [showUnimodModal, setShowUnimodModal] = useState(false);
    const modifiedSequence = spectrumAnnotation["Modified Sequence"] || spectrumAnnotation.Sequence;
    const fixedMods = parseModificationList(spectrumAnnotation["Fixed Modifications"]);
    const variableMods = parseModificationList(spectrumAnnotation["Variable Modifications"]);

    // Canonical modification description from the species' unimod DB, keyed by
    // Unimod accession. Falls back to whatever the caller supplied (raw
    // annotation label, curated name, or "Unknown") when not in the DB.
    const dbDescription = id => {
        const u = (id != null) ? unimodById.get(Number(id)) : null;
        return u && u.full_name ? u.full_name : null;
    };
    const rowLabel = (id, fallback) => dbDescription(id) || fallback || "Unknown";
    // Modification-mass offset (Da) from the unimod DB, used in the table.
    const dbMass = id => {
        const u = (id != null) ? unimodById.get(Number(id)) : null;
        return u && typeof u.mass === 'number' ? u.mass : null;
    };
    // When the site's mass class holds several equally-valid Unimod candidates
    // (same specificity, position scope, mass), return all of them so BOTH the
    // name and the Unimod columns can list every possibility; otherwise just
    // the single ID.
    const candidateIdsFor = mod => {
        if (mod.unimodId == null) return [];
        const ids = unimodIdToCandidates.get(Number(mod.unimodId));
        return ids && ids.length > 1 ? ids : [Number(mod.unimodId)];
    };

    // Human-readable name for every candidate ID, mirroring the multiple IDs
    // shown in the Unimod column. Falls back to the parsed label for the
    // primary ID, then to the curated UNIMOD map, then "Unknown".
    const candidateNamesFor = mod => {
        const primary = Number(mod.unimodId);
        return candidateIdsFor(mod).map(uid =>
            dbDescription(uid) || unimodDisplayName(uid) || (uid === primary ? mod.label : "Unknown")
        );
    };
    const trypticity = classifyTrypticity(spectrumAnnotation.Sequence, protein_sequence);

    // Build one row per modification site: prefer per-site tokens parsed from
    // the modified sequence (each <U:N> becomes its own row with AA +
    // position). Fixed/Variable labels are used to classify each site's Type;
    // any label whose Unimod ID has no matching site in the sequence still
    // appears as a fallback row with blank AA/Position.
    const labelRows = [
        ...fixedMods.map(mod => ({...mod, type: "Fixed"})),
        ...variableMods.map(mod => ({...mod, type: "Variable"})),
    ].map(row => ({...row, unimodId: resolveUnimodId(row.label)}));

    const siteEntries = extractModificationSites(modifiedSequence);

    // Index labels by unimodId to classify sites; consume as we go so that a
    // label with N sites still yields N rows of the correct type.
    const remainingLabels = labelRows.map(r => ({...r, remaining: 1}));
    const findLabel = id => remainingLabels.find(r => r.unimodId === id);

    const siteRows = siteEntries.map(site => {
        const match = findLabel(site.unimodId);
        const label = rowLabel(site.unimodId, match ? match.label : (unimodDisplayName(site.unimodId) || "Unknown"));
        const type = match ? match.type : "From sequence";
        if (match) match.remaining = 0;   // mark as consumed for the "unmatched" pass
        return {
            type,
            label,
            unimodId: site.unimodId,
            mass: dbMass(site.unimodId),
            aa: site.aa,
            position: site.terminus === 'C-term' ? `${site.position} (C-term)` : site.position,
        };
    });

    // Any Fixed/Variable label whose Unimod ID never appeared in the sequence
    // still needs to be shown (e.g. the pipeline didn't inline <U:N> tokens).
    const consumedIds = new Set(siteEntries.map(s => s.unimodId));

    // Try to infer AA from label text for unmatched rows (e.g. "Pyrolidone from Q" -> Q)
    const inferAaFromLabel = (label, position) => {
        if (!label) return null;
        // Position 0 or "N-term" suggests N-terminal
        if (position === 0 || position === "0" || position === "N-term") return "N-term";
        // Patterns: "X from Y", "X on Y", "X at Y", "X of Y" -> Y is the AA
        const match = label.match(/\b(from|on|at|of)\s+([A-Z])\b/i);
        if (match) return match[2].toUpperCase();
        // Pattern: "X (Y)" where Y is single AA -> Y
        const parenMatch = label.match(/\(([A-Z])\)$/);
        if (parenMatch) return parenMatch[1].toUpperCase();
        return null;
    };

    const unmatchedLabelRows = labelRows
        .filter(r => r.unimodId == null || !consumedIds.has(r.unimodId))
        .map(r => ({
            type: r.type,
            label: rowLabel(r.unimodId, r.label),
            unimodId: r.unimodId,
            mass: dbMass(r.unimodId),
            aa: inferAaFromLabel(r.label, r.position),
            position: r.position,
        }));

    const modifications = [...siteRows, ...unmatchedLabelRows];

    return (
        <div className="mb-4 rounded border border-slate-200 bg-slate-50 p-3">
            <div className="text-sm text-slate-500 flex items-center gap-2">
                <span>Modified peptide</span>
                {trypticity === 'fully' && (
                    <span
                        title="Both termini follow tryptic cleavage rules (K/R, not before P)"
                        className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
                    >
                        fully enzymatic
                    </span>
                )}
                {trypticity === 'semi' && (
                    <span
                        title="Only one terminus follows tryptic cleavage rules (K/R, not before P)"
                        className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                    >
                        semi-tryptic
                    </span>
                )}
                {uniqueLabel && (
                    <>
                        <button
                            type="button"
                            title="Click for details about peptide uniqueness"
                            onClick={() => setShowUniqueModal(true)}
                            className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800 cursor-pointer hover:bg-sky-200"
                        >
                            {uniqueLabel}
                            <svg aria-hidden="true" className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                            </svg>
                        </button>
                        {showUniqueModal && (
                            <Modal title="Peptide uniqueness" onClose={() => setShowUniqueModal(false)}>
                                <div className="space-y-4 text-sm text-slate-700">
                                    <p>
                                        This flag indicates how specific the detected peptide is to this protein and
                                        whether the protein is a <b>canonical</b> (reference proteome) or a
                                        {" "}<b>non-canonical</b> (novel/alternative) product. It is set in the mass
                                        spectrometry pipeline when a peptide is mapped back to the proteins that
                                        contain its sequence.
                                    </p>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-200 text-left text-slate-500">
                                                <th className="py-1 pr-3 font-medium">Value</th>
                                                <th className="py-1 pr-3 font-medium">Meaning</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                ["0 · shared peptide between canonical proteins", "Peptide sequence is shared with other proteins; this protein belongs to the canonical (reference) proteome."],
                                                ["1 · unique to a canonical protein", "Peptide sequence maps to this protein only; this protein belongs to the canonical (reference) proteome."],
                                                ["2 · shared between non-canonical proteins", "Peptide sequence is shared with other proteins; this protein is a non-canonical (novel/alternative) product."],
                                                ["3 · unique to a non-canonical protein", "Peptide sequence maps to this protein only; this protein is a non-canonical (novel/alternative) product."],
                                            ].map(([code, meaning]) => (
                                                <tr key={code} className="border-b border-slate-100 last:border-0">
                                                    <td className="py-1 pr-3 font-mono text-slate-800">{code}</td>
                                                    <td className="py-1 pr-3">{meaning}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <p className="text-xs text-slate-500">
                                        A "common" peptide is less specific evidence for the protein it was found with:
                                        the underlying MS/MS spectrum could originate from any protein sharing the same
                                        sequence. Real uniqueness also depends on whether the peptide is a fully or
                                        semi-tryptic match.
                                    </p>
                                </div>
                            </Modal>
                        )}
                    </>
                )}
            </div>
            {showUnimodModal && (
                <Modal title="UniMod identifiers" onClose={() => setShowUnimodModal(false)}>
                    <div className="space-y-4 text-sm text-slate-700">
                        <p>
                            The <b>Unimod</b> column lists accession IDs from the
                            {" "}<a href="https://www.unimod.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">UniMod</a>{" "}
                            database that describe each post-translational modification.
                        </p>
                        <p>
                            In mass spectrometry searches the search engine identifies
                            modifications by their <b>mass shift</b> — it cannot distinguish
                            chemically different modifications that share the same monoisotopic
                            mass (or whose masses fall within the instrument's precision).
                            This applies to both closed and open (DMO) searches.
                        </p>
                        <p>
                            The pipeline therefore groups every UniMod entry that shares the same
                            amino-acid specificity, position scope, and mass into a single
                            {" "}<em>mass class</em>.  When a site is assigned to one of these
                            classes, <b>all</b> candidate UniMod accessions in that class are
                            shown as equally valid matches.
                        </p>
                        <p className="text-xs text-slate-500">
                            If only one UniMod accession exists for a given mass class, only that
                            single ID is displayed.
                        </p>
                    </div>
                </Modal>
            )}
            <div className="font-mono text-base text-slate-900 break-all">
                {modifiedSequence ? toProForma(modifiedSequence) : "None reported"}
            </div>
            <table className="mt-3 w-full text-sm">
                <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500">
                        <th className="py-1 pr-3 font-medium">Type</th>
                        <th className="py-1 pr-3 font-medium">Modification</th>
                        <th className="py-1 pr-3 font-medium text-right">Mass</th>
                        <th
                            className="py-1 pr-3 font-medium cursor-pointer hover:underline"
                            onClick={() => setShowUnimodModal(true)}
                            title="Why may more than one UniMod ID be listed?"
                        >
                            Unimod
                            <svg aria-hidden="true" className="inline h-3 w-3 ml-0.5 text-slate-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                            </svg>
                        </th>
                        <th className="py-1 pr-3 font-medium">AA</th>
                        <th className="py-1 pr-3 font-medium">Position</th>
                    </tr>
                </thead>
                <tbody>
                    {modifications.length > 0 ? modifications.map((mod, index) => (
                        <tr key={`${mod.type}-${mod.label}-${mod.position ?? 'x'}-${index}`} className="border-b border-slate-100 last:border-0">
                            <td className="py-1 pr-3">{mod.type}</td>
                            <td className="py-1 pr-3">
                                {candidateNamesFor(mod).length > 1 ? (
                                    <span className="flex flex-wrap">
                                        {candidateNamesFor(mod).map((name, i) => (
                                            <span key={`${name}-${i}`} className="mr-1.5">
                                                {i > 0 && <span className="mr-1.5 text-slate-300">/</span>}
                                                {name}
                                            </span>
                                        ))}
                                    </span>
                                ) : mod.label}
                            </td>
                            <td className="py-1 pr-3 text-right font-mono">{typeof mod.mass === 'number' ? mod.mass.toFixed(4) : <span className="text-slate-400">—</span>}</td>
                            <td className="py-1 pr-3"><UnimodCell id={mod.unimodId} candidates={unimodIdToCandidates.get(mod.unimodId)} /></td>
                            <td className="py-1 pr-3 font-mono">{mod.aa ?? <span className="text-slate-400">—</span>}</td>
                            <td className="py-1 pr-3 font-mono">{mod.position ?? <span className="text-slate-400">—</span>}</td>
                        </tr>
                    )) : (
                        <tr>
                            <td className="py-1 pr-3 text-slate-500" colSpan="6">
                                No fixed or variable modifications reported
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

const Selector = ({label, items, selectedItem, onChange}) =>
    <Select
        label={label}
        onChange={e => onChange(e.target.value)}
        value={selectedItem}
    >
      {
        items.map((item) => (
            <option key={item} value={item}>
                {item}
            </option>
        ))
      }
    </Select>

export default SpectrumViewer
